import React, { Suspense } from "react";
import _ from "lodash";
import { connect } from "react-redux";
import { Route, Switch, withRouter, Redirect, matchPath } from "react-router-dom";

import BaseScreen from "../components/base/BaseScreen";

import { NavigationService } from "../navigation";

import { ActionTypes } from "../redux/actionTypes/screens/DefaultActionTypes";
import { Actions, reducer, INITIAL_STATE } from "../redux/screens/DefaultRedux";
import { Actions as SessionActions } from "../redux/SessionRedux";
import { Actions as TaskActions } from "../redux/TaskRedux";
import { withTranslation } from "react-i18next";
import UIDs from "../UIDs";
import { hideInitPageLoadingIcon } from "../utils/Helper.js";
import NotFound from "../layouts/errors/not-found/index.jsx";
import SchoolSelectionPage from "./SchoolSelectionPage";
import DomainUtils from "../utils/DomainUtils";
import appConfig from "../config/AppConfig";

const uid = UIDs.screens.default;

//const PlainLayout = React.lazy(() => import("../layouts/PlainLayout"));
const AccountLayout = React.lazy(() => import("../layouts/account/layout.jsx"));
// const DashBoardLayout = React.lazy(() => import("../layouts/DashBoardLayout"));
// const MainLayout = React.lazy(() => import("../layouts/MainLayout"));
const AdminLayout = React.lazy(() => import("../layouts/admin/layout.jsx"));
const PortalLayout = React.lazy(() => import("../layouts/portal/layout.jsx"));

// 过滤特殊路由片段，避免误触发 changeNetwork
const excludedUrlPart = ["account", "portal", "initial", "error", "admin"];

class DefaultScreen extends BaseScreen {
    get routeName() {
        return uid;
    }

    get reducer() {
        return reducer;
    }

    get actionHandlers() {
        return {
            [ActionTypes.LOAD_APP_ASYNC]: { done: this._handleLoadAppAsyncDone, error: null },
        };
    }

    componentDidMount() {
        super.componentDidMount();
        this.props.tryLoadSession();
        this.props.loadTask();
    }


    _syncUrl = (networkId) => {
        const { history, location, customDomain } = this.props;


        // Check if we're in subdomain mode
        if (DomainUtils.isSubdomainMode()) {
            const currentSubdomainId = DomainUtils.getSubDomain();

            if (currentSubdomainId !== customDomain) {
                // Need to redirect to different subdomain
                // Build full URL including /rec/ prefix for subdomain
                const currentPath = appConfig.PUBLIC_PREFIX.replace(/\/$/, '') + location.pathname;
                const newUrl = DomainUtils.buildSubdomainUrl(currentSubdomainId, currentPath + location.search + location.hash);
                window.location.href = newUrl;
                return;
            }
            if (!networkId) {
                // Router basename handles /rec/ prefix, so we just use the internal route
                history.replace("/");
                return;
            }
            // Same subdomain, no need to change URL
            return;
        }
        if (!networkId) {
            // Router basename handles /rec/ prefix, so we just use the internal route
            history.replace("/initial/schoolselection");
            return;
        }

        // Legacy path parameter mode - Router basename handles /rec/, so internal route is /:networkId/...
        const match = matchPath(location.pathname, { path: "/:networkId", exact: false, strict: false });
        if (match.params.networkId && match.params.networkId !== networkId && !excludedUrlPart.includes(match.params.networkId)) {
            const restPath = match && match.url ? location.pathname.slice(match.url.length) : "";
            history.replace(`/${networkId}${restPath}${location.search}${location.hash}`);
        }
    };

    componentDidUpdate(prevProps) {
        super.componentDidUpdate(prevProps);
        const {
            localState: { networkToSync, sessionLoaded },
            currentSchoolId,
            shortId,
            history,
            location,
        } = this.props; // removed unused match

        const prevNetworkToSync = prevProps.localState.networkToSync; // networkToSync from NetworkSyncRedux
        if (networkToSync && networkToSync !== prevNetworkToSync) {
            this._syncUrl(networkToSync);
            this.props.clearNetworkToSync();
            return;
        }

        const prevSessionLoaded = prevProps.localState.sessionLoaded;

        // Get network ID from URL - support both subdomain and path parameter modes
        const getCurrentUrlNetworkId = () => {
            // Priority 1: Get from subdomain
            const subdomainId = DomainUtils.getSubDomain();
            if (subdomainId) {
                return subdomainId;
            }

            // Priority 2: Get from path parameter (legacy) - Router basename handles /rec/, so pattern is /:networkId
            const m = matchPath(location.pathname, { path: "/:networkId", exact: false, strict: false });
            return m && m.params ? m.params.networkId : undefined;
        };

        const getPrevUrlNetworkId = () => {
            // For previous props, we can only check path parameters since subdomain wouldn't change
            const m = matchPath(prevProps.location.pathname, { path: "/:networkId", exact: false, strict: false });
            return m && m.params ? m.params.networkId : undefined;
        };

        const urlParam = getCurrentUrlNetworkId();
        const prevUrlParam = getPrevUrlNetworkId();
        const desiredNetworkId = shortId || currentSchoolId;


        // sessionLoaded 从 true 到 false 时处理登出或会话失效
        if (prevSessionLoaded && !sessionLoaded) {
            if (DomainUtils.isSubdomainMode()) {
                // Router basename handles /rec/ prefix, so we just use the internal route
                history.replace("/");
            }else{
                // Router basename handles /rec/ prefix, so we just use the internal route
                history.replace("/initial/schoolselection");
            }

            return;
        }

        // sessionLoaded 从 false 到 true 时进行路由同步
        if (!prevSessionLoaded && sessionLoaded) {
            if (!currentSchoolId && !urlParam) {
                if (DomainUtils.isSubdomainMode()) {
                    // Router basename handles /rec/ prefix, so we just use the internal route
                    history.replace("/");
                }else{
                    // Router basename handles /rec/ prefix, so we just use the internal route
                    history.replace("/initial/schoolselection");
                }
            } else if (currentSchoolId && urlParam && urlParam !== desiredNetworkId) {
                this._syncUrl(desiredNetworkId);
            } else if (!desiredNetworkId && urlParam && !excludedUrlPart.includes(urlParam)) {
                // Only trigger changeNetwork for valid school IDs (not system routes)
                this.props.changeNetwork({ currentSchoolId: currentSchoolId, shortId: urlParam });
            }

            hideInitPageLoadingIcon();
        }
    }

    renderSubdomainRoutes() {
        const schoolId = DomainUtils.getCurrentSchoolId();

        return (
            <Switch>
                {/* 公共路径（无需学校ID） - 放在最前面确保不被拦截 */}
                {/*<Route path="/initial/schoolselection" component={SchoolSelectionPage} />*/}
                <Route path="/account" component={AccountLayout} />
                <Route path="/error/notfound" component={NotFound} />

                {/* 根路径默认显示portal */}
                <Route exact path="/" render={() => (
                    <PortalLayout urlNetworkId={schoolId} />
                )} />

                {/* 子域名模式路径（无networkId在URL中） */}
                <Route path="/portal" render={(routeProps) => (
                    <PortalLayout {...routeProps} urlNetworkId={schoolId} />
                )} />
                <Route path="/admin" render={(routeProps) => (
                    <AdminLayout {...routeProps} urlNetworkId={schoolId} />
                )} />

                {/* 检测并重定向冗余的networkId路径 - 放在具体路径之后 */}
                <Route path={`/${schoolId}/*`} render={({ location }) => {
                    // Remove redundant networkId from URL
                    // /hust/admin/facility -> /admin/facility
                    const newPath = location.pathname.replace(`/${schoolId}`, '');
                    const finalPath = newPath || '/'; // If empty, go to root
                    return <Redirect to={finalPath + location.search + location.hash} />;
                }} />

                {/* 兜底错误处理 - 放在最后 */}
                <Route path="/*" component={NotFound} />
            </Switch>
        );
    }

    renderLegacyRoutes() {
        return (
            <Switch>
                {/* 公共路径（无需学校ID） */}
                <Route path="/initial/schoolselection" component={SchoolSelectionPage} />
                <Route path="/account" component={AccountLayout} />

                {/* 传统模式路径（包含networkId） */}
                <Route path="/:networkId/portal" render={(routeProps) => (
                    <PortalLayout {...routeProps} urlNetworkId={routeProps.match.params.networkId} />
                )} />
                <Route path="/:networkId/admin" render={(routeProps) => (
                    <AdminLayout {...routeProps} urlNetworkId={routeProps.match.params.networkId} />
                )} />
                <Route exact path="/:networkId" render={({ match }) => (
                    <Redirect to={`/${match.params.networkId}/portal/offerings`} />
                )} />

                {/* 错误处理 */}
                <Route path="/:networkId/error/notfound" component={NotFound} />
                <Route path="/:networkId/*" component={NotFound} />

                {/* 根路径兜底 */}
                <Route exact path="/" component={SchoolSelectionPage} />
            </Switch>
        );
    }

    render() {
        const isSubdomainMode = DomainUtils.isSubdomainMode();
        const hasSchoolId = DomainUtils.getCurrentSchoolId();

        return (
            <>
                <Suspense fallback={<div className="loader"></div>}>
                    {/* 根据模式渲染不同的路由配置 */}
                    {isSubdomainMode && hasSchoolId ?
                        this.renderSubdomainRoutes() :
                        this.renderLegacyRoutes()
                    }
                </Suspense>
            </>
        );
    }

    _handleLoadAppAsyncDone = () => {
        const { userId, location, currentSchoolId, shortId } = this.props; // 获取 networkId 从 state 而非路由
        const networkId = shortId || currentSchoolId;
        const basePath = networkId ? `/${networkId}` : "";

        // Ensure NavigationService can prepend networkId or basePath to UIDs or paths
        // This might require modifying NavigationService or how UIDs resolve to paths.
        // For now, assuming NavigationService.navigateToPageByUID might need networkId context.

        if (
            !this.isLoggedIn(userId) &&
            !_.startsWith(location.pathname, `${basePath}/account/`) &&
            !_.startsWith(location.pathname, `${basePath}/portal/`) &&
            !_.startsWith(location.pathname, `${basePath}/initial/`)
        ) {
            // TODO: Confirm NavigationService.navigateToPageByUID can correctly build URL with networkId
            NavigationService.navigateToPageByUID(this, UIDs.screens.portal.offerings); // Pass networkId if service supports it
        } else if ((location.pathname === basePath || location.pathname === `${basePath}/`) && !_.startsWith(location.pathname, `${basePath}/initial/`)) {
            // If path is just /:networkId/ or /:networkId
            // TODO: Confirm NavigationService.navigateToPageByUID can correctly build URL with networkId
            NavigationService.navigateToPageByUID(this, UIDs.screens.admin.dashboard); // Pass networkId if service supports it
        }
        hideInitPageLoadingIcon();
    };
}

const mapStateToProps = (state) => {
    return {
        localState: state.screens[reducer.key] ? state.screens[reducer.key] : INITIAL_STATE,
        userId: state.session.userId,
        mytheme: state.theme.mytheme,
        currentSchoolId: state.session.currentSchoolId,
        shortId: state.session.shortId,
        customDomain: state.session.customDomain,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        loadAppAsync: () => dispatch(Actions.loadAppAsync()),
        tryLoadSession: () => dispatch(SessionActions.tryLoadSession()),
        loadTask: () => dispatch(TaskActions.loadTask()),
        changeNetwork: (payload) => dispatch(SessionActions.changeNetwork(payload)),
        clearNetworkToSync: () => dispatch(Actions.clearNetworkToSync()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withTranslation(uid)(DefaultScreen)));
